import { type FC, useEffect, useRef, useState } from "react";
import { FiMail } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Container } from "../components/Container";
import { Notice } from "../components/Notice";
import { PageHeading } from "../components/PageHeading";
import { StatusHeader } from "../components/StatusHeader";
import { useMe } from "../hooks/useMe";
import { authApi } from "../rpc";

/**
 * メールのマジックリンク (`?lid=&t=`) から来たときの自動ログイン。
 *
 * マジックリンクは multi-use (検証しても失効しない) なので、メールスキャナ等の prefetch が
 * 先に踏んでもリンクは死なない。したがって「ログインを完了する」ボタンで人手クリックを挟む
 * 必要はなく、ページ表示と同時に検証 → ダッシュボードへリダイレクトする。
 */
const AutoVerifyLogin: FC<{ lid: string; t: string }> = ({ lid, t }) => {
  const navigate = useNavigate();
  const { refetch } = useMe();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode の二重実行 / 再レンダを1回に抑える
    ran.current = true;
    (async () => {
      try {
        const res = await authApi.verify.$post({ json: { lid, t } });
        if (res.status === 200) {
          await refetch();
          navigate("/dashboard", { replace: true });
          return;
        }
        const data = await res.json();
        setError(
          "message" in data
            ? data.message
            : "リンクの有効期限が切れているか、無効です。再度送信してください。",
        );
      } catch {
        setError("認証に失敗しました。しばらくしてから再度お試しください。");
      }
    })();
  }, [lid, t, navigate, refetch]);

  if (error) {
    return (
      <Container>
        <PageHeading title="ログインできませんでした" />
        <Notice variant="info" className="mb-6">
          <p className="text-neutral-600">{error}</p>
          <p className="mt-3">
            <Link to="/login" className="underline underline-offset-4 hover:text-neutral-900">
              もう一度ログインリンクを送る
            </Link>
          </p>
        </Notice>
      </Container>
    );
  }

  return (
    <Container>
      <StatusHeader tone="info" icon={<FiMail size={28} />} title="ログインしています…">
        <p>少々お待ちください。 自動的にマイページへ移動します。</p>
      </StatusHeader>
    </Container>
  );
};

export const VerifyPage: FC = () => {
  const [params] = useSearchParams();
  const lid = params.get("lid");
  const t = params.get("t");
  const email = params.get("email") ?? "";

  // メールリンクから来た場合: 自動でトークン検証 → ダッシュボード遷移
  if (lid && t) {
    return <AutoVerifyLogin lid={lid} t={t} />;
  }

  // ログインリンク送信済みの確認画面 (lid/t なし)
  return (
    <Container>
      <StatusHeader tone="info" icon={<FiMail size={28} />} title="メールを確認してください">
        <p>
          {email
            ? `${email} 宛にログイン用のリンクをお送りしました。リンクをクリックしてログインを完了してください。リンクは 15 分間有効です。`
            : "ご登録のメールアドレス宛にログイン用のリンクをお送りしました。リンクをクリックしてログインを完了してください。"}
        </p>
      </StatusHeader>
      <Notice variant="info" title="メールが届かない場合">
        <ul className="list-disc space-y-1 pl-5">
          <li>迷惑メールフォルダもご確認ください。</li>
          <li>メールアドレスに誤りがないか、もう一度お確かめください。</li>
          <li>
            それでも届かない場合は{" "}
            <Link to="/login" className="underline underline-offset-4 hover:text-neutral-900">
              再送
            </Link>
            してみてください。
          </li>
        </ul>
      </Notice>
    </Container>
  );
};
